import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { GrMicrophone } from "react-icons/gr";
import { MdAttachFile } from "react-icons/md";
import { RiArrowDropUpLine } from "react-icons/ri";
import { FiSend } from "react-icons/fi";
import { baseUrl } from '../config';

const MessageInput = ({ onSendMessage, theme = 'light', disabled = false }) => {
  const [message, setMessage] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [isRecording, setIsRecording] = useState(false);

  const handleSendMessage = () => {
    if (message.trim() && !disabled) {
      onSendMessage(message);
      setMessage('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleDropdownToggle = () => {
    setShowOptions(!showOptions);
  };

  const handleVoiceInput = () => {
    setIsRecording(!isRecording);
  };

  return (
    <>
      <style>{`
        /* Message Input Container - Fixed to Bottom */
        .pb-msginput-container {
          width: 100%;
          flex-shrink: 0;
          border-top: 1px solid;
          position: relative;
          z-index: 100;
        }

        .pb-msginput-theme-light {
          background-color: #ffffff;
          border-color: #e5e7eb;
        }

        .pb-msginput-theme-dark {
          background-color: #1f2937;
          border-color: #374151;
        }

        .pb-msginput-theme-read {
          background-color: #fef3c7;
          border-color: #fcd34d;
        }

        .pb-msginput-inner {
          max-width: 100%;
          padding: 16px 24px;
          margin: 0;
        }

        .pb-msginput-wrapper {
          position: relative;
          width: 100%;
        }

        /* Dropdown Options */
        .pb-msginput-dropdown {
          position: absolute;
          bottom: 100%;
          left: 0;
          margin-bottom: 8px;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          border: 1px solid;
          overflow: hidden;
          min-width: 200px;
          z-index: 1000;
        }

        .pb-msginput-theme-light .pb-msginput-dropdown {
          background-color: white;
          border-color: #e5e7eb;
        }

        .pb-msginput-theme-dark .pb-msginput-dropdown {
          background-color: #374151;
          border-color: #4b5563;
        }

        .pb-msginput-theme-read .pb-msginput-dropdown {
          background-color: #fef3c7;
          border-color: #fcd34d;
        }

        .pb-msginput-dropdown-option {
          padding: 12px 16px;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .pb-msginput-theme-light .pb-msginput-dropdown-option:hover {
          background-color: #f9fafb;
        }

        .pb-msginput-theme-dark .pb-msginput-dropdown-option:hover {
          background-color: #4b5563;
        }

        .pb-msginput-theme-read .pb-msginput-dropdown-option:hover {
          background-color: #fde68a;
        }

        /* Input Content Container */
        .pb-msginput-content {
          display: flex;
          align-items: flex-end;
          gap: 12px;
          width: 100%;
        }

        /* Text Input Area */
        .pb-msginput-field-wrapper {
          flex: 1;
          border-radius: 16px;
          border: 2px solid;
          transition: border-color 0.2s;
          min-width: 0;
        }

        .pb-msginput-theme-light .pb-msginput-field-wrapper {
          background-color: #f9fafb;
          border-color: #d1d5db;
        }

        .pb-msginput-theme-light .pb-msginput-field-wrapper:focus-within {
          border-color: #14b8a6;
        }

        .pb-msginput-theme-dark .pb-msginput-field-wrapper {
          background-color: #374151;
          border-color: #4b5563;
        }

        .pb-msginput-theme-dark .pb-msginput-field-wrapper:focus-within {
          border-color: #0d9488;
        }

        .pb-msginput-theme-read .pb-msginput-field-wrapper {
          background-color: #fef3c7;
          border-color: #fcd34d;
        }

        .pb-msginput-theme-read .pb-msginput-field-wrapper:focus-within {
          border-color: #d97706;
        }

        .pb-msginput-field-inner {
          display: flex;
          align-items: center;
          padding: 12px 16px;
          width: 100%;
        }

        /* Textarea */
        .pb-msginput-textarea {
          flex: 1;
          outline: none;
          resize: none;
          min-height: 24px;
          max-height: 120px;
          border: none;
          background: transparent;
          font-size: 15px;
          line-height: 1.5;
          font-family: inherit;
        }

        .pb-msginput-theme-light .pb-msginput-textarea {
          color: #111827;
        }

        .pb-msginput-theme-light .pb-msginput-textarea::placeholder {
          color: #9ca3af;
        }

        .pb-msginput-theme-dark .pb-msginput-textarea {
          color: #f9fafb;
        }

        .pb-msginput-theme-dark .pb-msginput-textarea::placeholder {
          color: #6b7280;
        }

        .pb-msginput-theme-read .pb-msginput-textarea {
          color: #78350f;
          font-family: Georgia, 'Times New Roman', serif;
          font-size: 16px;
        }

        .pb-msginput-theme-read .pb-msginput-textarea::placeholder {
          color: #92400e;
        }

        .pb-msginput-textarea:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Icon Buttons */
        .pb-msginput-icons {
          display: flex;
          align-items: center;
          gap: 4px;
          margin-left: 8px;
          flex-shrink: 0;
        }

        .pb-msginput-icon-btn {
          padding: 8px;
          border-radius: 9999px;
          background: none;
          border: none;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .pb-msginput-theme-light .pb-msginput-icon-btn {
          color: #6b7280;
        }

        .pb-msginput-theme-light .pb-msginput-icon-btn:hover {
          background-color: #e5e7eb;
          color: #111827;
        }

        .pb-msginput-theme-dark .pb-msginput-icon-btn {
          color: #9ca3af;
        }

        .pb-msginput-theme-dark .pb-msginput-icon-btn:hover {
          background-color: #4b5563;
          color: #f9fafb;
        }

        .pb-msginput-theme-read .pb-msginput-icon-btn {
          color: #92400e;
        }

        .pb-msginput-theme-read .pb-msginput-icon-btn:hover {
          background-color: #fde68a;
          color: #78350f;
        }

        .pb-msginput-icon {
          font-size: 20px;
        }

        .pb-msginput-dropdown-icon {
          transition: transform 0.2s;
        }

        .pb-msginput-dropdown-icon.pb-msginput-rotated {
          transform: rotate(180deg);
        }

        /* Recording State */
        .pb-msginput-recording {
          background-color: #ef4444 !important;
          color: white !important;
          animation: pb-msginput-pulse 1.5s infinite;
        }

        @keyframes pb-msginput-pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.7;
          }
        }

        /* Send Button */
        .pb-msginput-send-btn {
          padding: 16px;
          border-radius: 16px;
          border: none;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          flex-shrink: 0;
        }

        .pb-msginput-theme-light .pb-msginput-send-btn {
          background-color: #14b8a6;
          color: white;
        }

        .pb-msginput-theme-light .pb-msginput-send-btn:hover:not(:disabled) {
          background-color: #0d9488;
          transform: scale(1.05);
        }

        .pb-msginput-theme-dark .pb-msginput-send-btn {
          background-color: #0d9488;
          color: white;
        }

        .pb-msginput-theme-dark .pb-msginput-send-btn:hover:not(:disabled) {
          background-color: #0f766e;
          transform: scale(1.05);
        }

        .pb-msginput-theme-read .pb-msginput-send-btn {
          background-color: #d97706;
          color: white;
        }

        .pb-msginput-theme-read .pb-msginput-send-btn:hover:not(:disabled) {
          background-color: #b45309;
          transform: scale(1.05);
        }

        .pb-msginput-send-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .pb-msginput-send-icon {
          font-size: 20px;
        }

        /* Character Count */
        .pb-msginput-char-count {
          font-size: 12px;
          text-align: right;
          margin-top: 8px;
        }

        .pb-msginput-theme-light .pb-msginput-char-count {
          color: #6b7280;
        }

        .pb-msginput-theme-dark .pb-msginput-char-count {
          color: #9ca3af;
        }

        .pb-msginput-theme-read .pb-msginput-char-count {
          color: #92400e;
        }

        /* Disabled Warning */
        .pb-msginput-warning {
          text-align: center;
          margin-top: 8px;
        }

        .pb-msginput-warning-text {
          font-size: 14px;
          color: #ef4444;
          font-weight: 500;
          margin: 0;
        }
      `}</style>

      <div className={`pb-msginput-container pb-msginput-theme-${theme}`}>
        <div className="pb-msginput-inner">
          <div className="pb-msginput-wrapper">
            {/* Dropdown Options */}
            {showOptions && (
              <div className="pb-msginput-dropdown">
                {/* <div className="pb-msginput-dropdown-option">
                  <span>💰 Budget Analysis</span>
                </div> */}
                <div className="pb-msginput-dropdown-option">
                  <span>📄 Upload Documents</span>
                </div>
                <div className="pb-msginput-dropdown-option">
                  <span>🎬 Scene Templates</span>
                </div>
              </div>
            )}

            {/* Input Content */}
            <div className="pb-msginput-content">
              {/* Text Input Area */}
              <div className="pb-msginput-field-wrapper">
                <div className="pb-msginput-field-inner">
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={disabled ? "Daily limit reached. You can only improve existing scripts." : "Type your message..."}
                    disabled={disabled}
                    className="pb-msginput-textarea"
                    rows="1"
                  />
                  
                  {/* Icon Buttons */}
                  <div className="pb-msginput-icons">
                    <button
                      onClick={handleDropdownToggle}
                      className="pb-msginput-icon-btn"
                      title="More options"
                    >
                      <RiArrowDropUpLine className={`pb-msginput-icon pb-msginput-dropdown-icon ${showOptions ? 'pb-msginput-rotated' : ''}`} />
                    </button>
                    
                    <button
                      className="pb-msginput-icon-btn"
                      title="Attach file"
                    >
                      <MdAttachFile className="pb-msginput-icon" />
                    </button>
                    
                    <button
                      onClick={handleVoiceInput}
                      className={`pb-msginput-icon-btn ${isRecording ? 'pb-msginput-recording' : ''}`}
                      title={isRecording ? "Stop recording" : "Voice input"}
                    >
                      <GrMicrophone className="pb-msginput-icon" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Send Button */}
              <button
                onClick={handleSendMessage}
                disabled={!message.trim() || disabled}
                className="pb-msginput-send-btn"
                title="Send message"
              >
                <FiSend className="pb-msginput-send-icon" />
              </button>
            </div>

            {/* Character Count */}
            {message.length > 0 && (
              <div className="pb-msginput-char-count">
                {message.length} characters
              </div>
            )}

            {/* Disabled Warning */}
            {disabled && (
              <div className="pb-msginput-warning">
                <p className="pb-msginput-warning-text">
                  ⚠️ You've reached your daily limit of 2 scripts. You can improve existing scripts without limits!
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

MessageInput.propTypes = {
  onSendMessage: PropTypes.func.isRequired,
  theme: PropTypes.string,
  disabled: PropTypes.bool
};

export default MessageInput;
